import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Activity, ArrowUpRight, CheckCircle2, FileDown, Loader2, MinusCircle, PlayCircle,
    ShieldAlert, Stethoscope, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { ModuleGate } from "@/components/common/ModuleGate";
import { EngineHero } from "@/components/common/EngineHero";
import { SeverityBadge } from "@/components/common/Badges";
import { staggerContainer, staggerItem } from "@/lib/motion";
import { TID } from "@/lib/testIds";

const tone = (v) =>
    v === null || v === undefined ? { text: "text-slate-400", bar: "rgba(148,163,184,0.35)" }
        : v >= 85 ? { text: "text-emerald-400", bar: "var(--safe)" }
            : v >= 60 ? { text: "text-amber-400", bar: "var(--warning)" }
                : { text: "text-rose-400", bar: "var(--neon-red)" };

const EFFORT_LABEL = { low: "quick win", medium: "planned change", high: "project" };

function MaturityBand({ level, band, note, coverage, measured, total }) {
    const t = tone(level * 20);
    return (
        <div data-testid={TID.diagMaturity} className="flex flex-wrap items-center gap-4">
            <div className="flex items-baseline gap-2">
                <span className={`text-5xl font-mono ${t.text}`}>{level}</span>
                <span className="text-slate-400 text-sm font-mono">/5</span>
            </div>
            <div className="min-w-0">
                <p className={`text-lg font-mono uppercase tracking-wider ${t.text}`}>{band}</p>
                <p className="text-sm text-slate-400 max-w-2xl">{note}</p>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-1">
                    evidence coverage {coverage}% · {measured}/{total} domains measured
                </p>
            </div>
        </div>
    );
}

function DomainCard({ d, onDrill }) {
    const t = tone(d.score);
    return (
        <motion.div
            variants={staggerItem}
            data-testid={TID.diagDomain(d.key)}
            className="panel p-4 rounded-[4px] space-y-2.5"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-sm text-slate-100">{d.label}</p>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">weight {d.weight}</p>
                </div>
                {d.measured ? (
                    <span className={`text-2xl font-mono ${t.text}`}>{d.score}</span>
                ) : (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <MinusCircle size={11} /> not measured
                    </span>
                )}
            </div>

            <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
                <motion.div
                    className="h-full rounded-full"
                    style={{ background: t.bar }}
                    initial={{ width: 0 }}
                    animate={{ width: `${d.measured ? d.score : 0}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                />
            </div>

            <p className="text-[11px] text-slate-400">{d.basis}</p>

            {(d.actions || []).slice(0, 2).map((a) => (
                <div key={a.title} className="flex items-start gap-2 text-[11px]">
                    <SeverityBadge severity={a.severity} />
                    <span className="text-slate-300 flex-1">{a.title}</span>
                </div>
            ))}

            <button
                data-testid={TID.diagDomainDrill(d.key)}
                onClick={() => onDrill(d.drill_to)}
                className="btn-ghost inline-flex items-center gap-1 text-[10px]"
            >
                Open {d.label.split(" ")[0]} <ArrowUpRight size={11} />
            </button>
        </motion.div>
    );
}

function TrendStrip({ trend }) {
    if (!trend || trend.length < 2) return null;
    const max = 100;
    return (
        <div data-testid={TID.diagTrend} className="flex items-end gap-1.5 h-14">
            {trend.slice(-24).map((p, i) => {
                const t = tone(p.score);
                return (
                    <div key={`${p.created_at}-${i}`} className="w-3 shrink-0" data-testid={TID.diagTrendBar(i)}>
                        <div
                            className="w-full rounded-sm transition-all"
                            style={{ height: `${((p.score || 0) / max) * 56}px`, background: t.bar }}
                            title={`${p.created_at} · score ${p.score} · level ${p.level}`}
                        />
                    </div>
                );
            })}
        </div>
    );
}

export default function Diagnostics() {
    const [snapshot, setSnapshot] = useState(null);
    const [history, setHistory] = useState(null);
    const [running, setRunning] = useState(false);
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    const loadHistory = useCallback(async () => {
        try {
            const { data } = await api.get("/diagnostics");
            setHistory(data);
        } catch {
            setHistory(null);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/diagnostics/latest");
                setSnapshot(data);
            } catch {
                setSnapshot(null);
            } finally {
                setLoading(false);
            }
            loadHistory();
        })();
    }, [loadHistory]);

    const run = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/diagnostics/run");
            setSnapshot(data);
            await loadHistory();
            const prev = data.previous;
            toast.success(
                prev && prev.overall_score !== null
                    ? `Diagnostic complete · ${data.overall_score} (was ${prev.overall_score})`
                    : `Diagnostic complete · score ${data.overall_score}, maturity level ${data.maturity_level}`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Diagnostic run failed");
        } finally {
            setRunning(false);
        }
    };

    const downloadPdf = async () => {
        if (!snapshot) return;
        try {
            const res = await api.get(`/diagnostics/${snapshot.id}/report/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `sap_security_diagnostic_${snapshot.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Diagnostic report downloaded");
        } catch {
            toast.error("Report unavailable");
        }
    };

    return (
        <ModuleGate module="diagnostics" label="Intelligent SAP Security Diagnostic">
            <motion.div variants={staggerContainer} initial="hidden" animate="show" className="space-y-6" data-testid={TID.diagPage}>
                <EngineHero
                    testId={TID.diagHero}
                    engine="Intelligent Diagnostic"
                    title="SAP Security Diagnostic"
                    icon={Stethoscope}
                    blurb="One pass over every artifact this workspace holds — engineered roles, SoD analysis, critical access, custom code, OData, governance records, runtime traces and best-practice conformance — scored, weighted and turned into a prioritized remediation roadmap. Domains with no evidence are declared unmeasured rather than scored."
                    rules={(snapshot?.domains || []).map((d) => ({ label: d.label, category: d.key }))}
                    totals={Object.fromEntries((snapshot?.domains || []).map((d) => [d.key, (d.actions || []).length]))}
                />

                <div className="flex flex-wrap items-center gap-2">
                    <button
                        data-testid={TID.diagRun}
                        onClick={run}
                        disabled={running}
                        className="btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                    >
                        {running ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />}
                        {snapshot ? "Re-run diagnostic" : "Run diagnostic"}
                    </button>
                    {snapshot && (
                        <button
                            data-testid={TID.diagPdf}
                            onClick={downloadPdf}
                            className="btn-ghost inline-flex items-center gap-1.5 text-xs"
                        >
                            <FileDown size={13} /> Report (PDF)
                        </button>
                    )}
                </div>

                {loading ? (
                    <Panel title="Diagnostic"><p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</p></Panel>
                ) : !snapshot ? (
                    <Panel eyebrow="No diagnosis yet" title="Run your first diagnostic" testId={TID.diagEmpty}>
                        <p className="text-sm text-slate-400">
                            The diagnostic reads only what is already in this installation. Domains without evidence are
                            reported as <span className="font-mono text-cyan-300">not measured</span> instead of being
                            scored optimistically, so the first run doubles as an inventory of what you still need to upload.
                        </p>
                    </Panel>
                ) : (
                    <>
                        <Panel
                            eyebrow={`Run ${String(snapshot.created_at).slice(0, 16).replace("T", " ")} · ${snapshot.created_by || "—"}`}
                            title="Diagnosis"
                            testId={TID.diagVerdict}
                            action={
                                <div className="flex items-baseline gap-2">
                                    <span className={`text-3xl font-mono ${tone(snapshot.overall_score).text}`} data-testid={TID.diagScore}>
                                        {snapshot.overall_score ?? "—"}
                                    </span>
                                    <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">composite</span>
                                </div>
                            }
                        >
                            <div className="space-y-4">
                                <MaturityBand
                                    level={snapshot.maturity_level}
                                    band={snapshot.maturity_band}
                                    note={snapshot.maturity_note}
                                    coverage={snapshot.coverage_pct}
                                    measured={snapshot.measured_domains}
                                    total={snapshot.total_domains}
                                />
                                {history?.trend?.length > 1 && (
                                    <div className="space-y-1.5">
                                        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                                            <TrendingUp size={11} /> composite score across {history.trend.length} run(s)
                                        </p>
                                        <TrendStrip trend={history.trend} />
                                    </div>
                                )}
                                <p className="text-[11px] text-slate-400 flex items-start gap-1.5">
                                    <ShieldAlert size={12} className="mt-0.5 shrink-0 text-cyan-400" />
                                    {snapshot.method}
                                </p>
                            </div>
                        </Panel>

                        <Panel eyebrow="Prioritized" title="Remediation Roadmap" testId={TID.diagRoadmap}
                            action={<span className="text-[10px] font-mono text-slate-400">{snapshot.roadmap_total} action(s)</span>}>
                            {snapshot.roadmap.length === 0 ? (
                                <p className="text-sm text-emerald-300 flex items-center gap-2">
                                    <CheckCircle2 size={14} /> Every measured domain is clean — nothing to remediate.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {snapshot.roadmap.map((a, i) => (
                                        <div
                                            key={`${a.domain}-${a.title}`}
                                            data-testid={TID.diagRoadmapItem(i)}
                                            className="flex flex-wrap items-start gap-2 border-b border-white/5 pb-2 last:border-0"
                                        >
                                            <span className="font-mono text-[10px] text-slate-400 w-5 pt-0.5">{i + 1}</span>
                                            <SeverityBadge severity={a.severity} />
                                            <div className="flex-1 min-w-[240px]">
                                                <p className="text-sm text-slate-100">{a.title}</p>
                                                <p className="text-[11px] text-slate-400">{a.detail}</p>
                                                {a.evidence && (
                                                    <p className="text-[10px] font-mono text-slate-400 mt-0.5">evidence: {a.evidence}</p>
                                                )}
                                            </div>
                                            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-white/5 text-slate-300 border-white/10">
                                                {EFFORT_LABEL[a.effort] || a.effort}
                                            </span>
                                            <button
                                                data-testid={TID.diagRoadmapDrill(i)}
                                                onClick={() => nav(a.drill_to)}
                                                className="btn-ghost inline-flex items-center gap-1 text-[10px] whitespace-nowrap"
                                            >
                                                {a.module} <ArrowUpRight size={11} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Panel>

                        <Panel eyebrow="Per domain" title="Domain Scores" testId={TID.diagDomains}
                            action={<span className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5"><Activity size={11} /> weighted composite</span>}>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                {snapshot.domains.map((d) => (
                                    <DomainCard key={d.key} d={d} onDrill={(to) => nav(to)} />
                                ))}
                            </div>
                        </Panel>
                    </>
                )}
            </motion.div>
        </ModuleGate>
    );
}
